import { SkeletonSpinnerCentered } from '@civilgrid/ui/SkeletonLoader';
import { useEffect, useMemo } from 'react';
import { useAsyncFn } from 'react-use';
import { getCGSRegulatoryQuadrangleList } from '~/api/projects2';

interface Props {
  projectId: string;
}

export default function CGSRegulatoryMaps({ projectId }: Props) {
  const [dataLoader, loadData] = useAsyncFn(async (projectId: string) => {
    return (await getCGSRegulatoryQuadrangleList(projectId)) as {
      id: string;
      name: string;
      type: string;
      docLinks: { label: string; url: string }[];
    }[];
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    loadData(projectId);
  }, []);

  const content = useMemo(() => {
    if (dataLoader.loading) {
      return <SkeletonSpinnerCentered />;
    } else {
      if (!dataLoader.value) {
        return null;
      } else {
        const rows = dataLoader.value.map(({ id, name, type, docLinks }) => {
          const docEls = docLinks.map((d) => (
            <a
              key={d.url}
              href={d.url}
              target="_blank"
              rel="noreferrer noopener"
              className="text-blue-50 underline hover:text-blue-60"
            >
              {d.label}
            </a>
          ));
          return (
            <tr key={id} className="p-2 ">
              <td className="pr-6 align-text-top">
                <div className="">{name}</div>
                <div className="text-xs">{type}</div>
              </td>
              <td className="flex flex-col pb-4">{docEls}</td>
            </tr>
          );
        });
        return (
          <table>
            <thead>
              <tr>
                <th className="text-left">Quadrangle</th>
                <th className="text-left">Documents</th>
              </tr>
            </thead>
            <tbody>{rows}</tbody>
          </table>
        );
      }
    }
  }, [dataLoader.loading, dataLoader.value]);

  if (dataLoader.value?.length === 0) {
    return null;
  }

  return (
    <div className="group w-full">
      <div className=" flex flex-col py-2 ">
        <div className="w-4/5 text-lg font-bold">CGS Regulatory Maps</div>
        <div className="mb-4 text-xs text-secondary">
          CivilGrid is not responsible for the accuracy of this data since it is obtained directly from{' '}
          <a
            href="https://maps.conservation.ca.gov/cgs/informationwarehouse/regulatorymaps/"
            target="_blank"
            rel="noreferrer noopener"
            className="text-blue-50 underline hover:text-blue-60"
          >
            The Department of Conservation
          </a>
          .
        </div>
      </div>

      <div className="w-full">{content}</div>
    </div>
  );
}
